import { ApolloError } from '@apollo/client';
import { isFluctuating } from '@e2m-customer-portal/shared';
import { ErrorComponent } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';
import { addMinutes, eachDayOfInterval, getTime, Interval, isBefore, startOfToday, toDate } from 'date-fns';
import { FeedInValue, MarketLocation, MarketLocationType, SpotPriceForecast } from 'generated/graphql';
import { AxisSetExtremesEventObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts/highstock';
import HighchartsBoost from 'highcharts/modules/boost';
import { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mostCompactName } from 'utils/most-compact-name';
import { SERIES_IDS } from '../constants';
import { dateAxisFormatter, formatTooltip, tooltipPositioner, unitFormatter } from './utils';
import { useLocale } from '@e2m-customer-portal/hooks';

const FOUR_HOURS_MILLISECONDS = 4 * 3600 * 1000;

export enum MSCONSCodeColor {
  missing = '#FF0A119D',
  actual = '#4F9E309D',
  temporary = '#F8BE689D',
  replacement = '#2BAEFF9D',
}

export function colorOfMSCONSCode(code: string) {
  switch (code) {
    case '220':
      return MSCONSCodeColor.actual;
    case 'Z18':
      return MSCONSCodeColor.temporary;
    case '67':
      return MSCONSCodeColor.replacement;
    default:
      return MSCONSCodeColor.missing;
  }
}

interface FeedInChartProps {
  marketLocation: MarketLocation;
  dataInterval: Interval;
  values: Map<string, readonly FeedInValue[]> | undefined;
  spotPriceData: SpotPriceForecast | undefined;
  shownInterval?: Interval;
  onChangeShownInterval?: (interval: Interval) => void;
  isFifteenMinuteResolution?: boolean;
  error?: ApolloError;
  isMobile: boolean;
}

export function FeedInChart({
  marketLocation,
  dataInterval,
  values,
  spotPriceData,
  shownInterval,
  onChangeShownInterval,
  isFifteenMinuteResolution,
  error,
  isMobile = false,
}: Readonly<FeedInChartProps>): ReactElement<FeedInChartProps> {
  if (error) {
    return <ErrorComponent error={error} />;
  }
  const { t, i18n } = useTranslation();
  const { code: locale } = useLocale();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const sortedValues =
    values?.get(marketLocation.id) &&
    [...values.get(marketLocation.id)!].sort((a, b) => getTime(a.date) - getTime(b.date));
  const linkedSortedValues =
    marketLocation.linkedMarketLocation &&
    values?.get(marketLocation.linkedMarketLocation?.id) &&
    [...values.get(marketLocation.linkedMarketLocation.id)!].sort((a, b) => getTime(a.date) - getTime(b.date));

  HighchartsBoost(Highcharts);

  const forecast = useMemo(
    () =>
      sortedValues?.map((item) => [
        getTime(item.date),
        item.forecastedValue == null ? null : item.forecastedValue * 1000,
      ]),
    [sortedValues],
  );

  const measures = useMemo(
    () =>
      sortedValues?.map((item) => [getTime(item.date), item.measuredValue == null ? null : item.measuredValue * 1000]),
    [sortedValues],
  );

  const linkedMeasures = useMemo(
    () =>
      linkedSortedValues?.map((item) => [
        getTime(item.date),
        item.measuredValue == null ? null : item.measuredValue * 1000,
      ]),
    [linkedSortedValues],
  );

  const spotPrices = useMemo(
    () =>
      spotPriceData?.dayAheadForecasts.flatMap((slice) => [
        [getTime(slice.date), slice.price],
        [getTime(addMinutes(slice.date, 15)), slice.price],
        [getTime(addMinutes(slice.date, 30)), slice.price],
        [getTime(addMinutes(slice.date, 45)), slice.price],
      ]),
    [spotPriceData],
  );

  const quality: Highcharts.SeriesZonesOptionsObject[] = useMemo(
    () =>
      sortedValues
        ?.filter(
          (item, index, values) =>
            (index + 1 < values.length && item.msconsCode !== values[index + 1].msconsCode) ||
            index + 1 === values.length,
        )
        .map((item) => ({
          value: getTime(item.date),
          color: item.msconsCode == null ? MSCONSCodeColor.missing : colorOfMSCONSCode(item.msconsCode),
        })) ?? [],
    [sortedValues],
  );

  const linkedQuality: Highcharts.SeriesZonesOptionsObject[] = useMemo(
    () =>
      (
        linkedSortedValues
          ?.filter(
            (item, index, values) => index + 1 < values.length && item.msconsCode !== values[index + 1].msconsCode,
          )
          .map((item) => ({
            value: getTime(item.date),
            color: item.msconsCode == null ? MSCONSCodeColor.missing : colorOfMSCONSCode(item.msconsCode),
          })) ?? []
      ).concat({ value: getTime(dataInterval.end), color: MSCONSCodeColor.missing }),
    [linkedSortedValues],
  );

  const qualityData = useMemo(
    () =>
      sortedValues?.map((item) => [
        getTime(item.date),
        item.msconsCode != null || isBefore(item.date, startOfToday()) ? values?.size : null,
      ]),
    [sortedValues],
  );

  const linkedQualityData = useMemo(
    () =>
      linkedSortedValues?.map((item) => [
        getTime(item.date),
        item.msconsCode != null || isBefore(item.date, startOfToday()) ? 1 : null,
      ]),
    [linkedSortedValues],
  );

  const latestHistoricalSpotPrice = spotPriceData?.latestHistoricalSpotPrice
    ? getTime(spotPriceData.latestHistoricalSpotPrice)
    : undefined;

  const handleSetExtremes =
    onChangeShownInterval &&
    (({ min, max }: AxisSetExtremesEventObject) => onChangeShownInterval({ start: min, end: max }));

  const options: Highcharts.Options = {
    chart: {
      height: isMobile ? window.innerHeight * 0.4 : 460,
      scrollablePlotArea: isMobile ? { minWidth: 2000, scrollPositionX: 0 } : undefined,
      zooming: {
        type: 'x',
      },
      animation: true,
    },
    time: {
      useUTC: false,
      timezone: 'Europe/Berlin',
    },
    scrollbar: {
      enabled: false,
    },
    boost: {
      enabled: true,
      allowForce: true,
      pixelRatio: 1,
      seriesThreshold: 1,
      useGPUTranslations: true,
      usePreallocated: true,
    },
    credits: {
      enabled: false,
    },
    legend: {
      useHTML: true,
      enabled: true,
      layout: 'horizontal',
      align: isMobile ? 'center' : 'right',
      verticalAlign: isMobile ? 'bottom' : 'top',
      x: isMobile ? 0 : -10,
      y: isMobile ? 0 : -15,
      itemWidth: isMobile ? window.innerWidth / 2.5 : undefined,
      width: isMobile ? window.innerWidth / 1.25 : undefined,
      itemStyle: {
        textOverflow: 'visible',
      },
    },
    rangeSelector: {
      enabled: false,
    },
    tooltip: {
      enabled: !isMobile,
      useHTML: true,
      shared: true,
      outside: true,
      style: { zIndex: 1300 },
      positioner: tooltipPositioner,
      formatter: formatTooltip(
        mostCompactName(marketLocation.name, marketLocation.linkedMarketLocation?.name),
        t,
        locale,
        sortedValues,
        linkedSortedValues,
      ),
    },
    xAxis: [
      {
        type: 'datetime',
        gridLineWidth: 1,
        gridLineDashStyle: 'Dash',
        tickInterval: FOUR_HOURS_MILLISECONDS,
        max: getTime(shownInterval?.end ?? dataInterval.end),
        min: getTime(shownInterval?.start ?? dataInterval.start),
        startOnTick: false,
        labels: {
          useHTML: true,
          formatter: dateAxisFormatter(t),
        },
        events: {
          afterSetExtremes: handleSetExtremes,
        },
        scrollbar: { enabled: false },
      },
      {
        type: 'datetime',
        gridLineWidth: 1,
        gridLineColor: '#C8CBCE',
        max: getTime(shownInterval?.end ?? dataInterval.end),
        min: getTime(shownInterval?.start ?? dataInterval.start),
        tickInterval: FOUR_HOURS_MILLISECONDS * 6,
        startOnTick: false,
        linkedTo: 0,
        offset: 0,
        labels: {
          enabled: false,
        },
        scrollbar: { enabled: false },
      },
    ],
    yAxis: [
      {
        id: `y-${SERIES_IDS.forecast}-${SERIES_IDS.measurement}`,
        gridLineWidth: 1,
        visible: true,
        labels: { formatter: unitFormatter('kW', i18n.language) },
        opposite: false,
        allowDecimals: false,
        softMin: 0,
      },
      {
        id: `y-${SERIES_IDS.spotPrice}`,
        gridLineWidth: 1,
        visible: true,
        labels: { formatter: unitFormatter('€/MWh', i18n.language) },
        opposite: true,
        allowDecimals: false,
        softMin: 0,
      },
      { id: `y-${SERIES_IDS.quality}`, height: 8, top: '100%', visible: false },
    ],
    navigator: {
      enabled: !isMobile,
      adaptToUpdatedData: true,
      series: [
        {
          // type must be line for step to take effect
          type: 'line',
          step: 'left',
        },
      ],
    },
    series: [
      {
        type: 'line',
        id: SERIES_IDS.forecast,
        yAxis: `y-${SERIES_IDS.forecast}-${SERIES_IDS.measurement}`,
        name: isMobile
          ? t('MARKET_LOCATION_FEED_IN_CHART_FORECAST_MOBILE')
          : t('MARKET_LOCATION_FEED_IN_CHART_FORECAST'),
        step: 'left',
        zoneAxis: 'x',
        zones: [{ value: getTime(new Date()) }, { dashStyle: 'ShortDot' }],
        color: '#802922',
        marker: { enabled: eachDayOfInterval(shownInterval ?? dataInterval).length < 30, radius: 1, symbol: 'circle' },
        softThreshold: true,
        data: forecast,
        zIndex: 2,
        dataGrouping: {
          units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
        },
        visible: !isFluctuating(marketLocation.technologies),
        showInLegend: !isFluctuating(marketLocation.technologies),
      },
      {
        type: 'line',
        id: SERIES_IDS.measurement,
        yAxis: `y-${SERIES_IDS.forecast}-${SERIES_IDS.measurement}`,
        name:
          marketLocation.type === MarketLocationType.CONSUMER
            ? t('MARKET_LOCATION_FEED_IN_CHART_MEASUREMENT_LINKED')
            : t('MARKET_LOCATION_FEED_IN_CHART_MEASUREMENT'),
        step: 'left',
        color: '#2BAEFF',
        marker: { enabled: eachDayOfInterval(shownInterval ?? dataInterval).length < 30, radius: 1, symbol: 'circle' },
        softThreshold: true,
        data: measures,
        zIndex: 1,
        dataGrouping: {
          units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
        },
        visible: true,
        showInNavigator: isFluctuating(marketLocation.technologies),
      },
      ...(linkedMeasures
        ? [
            {
              type: 'line',
              id: SERIES_IDS.linkedMeasurement,
              yAxis: `y-${SERIES_IDS.forecast}-${SERIES_IDS.measurement}`,
              name:
                marketLocation.linkedMarketLocation?.type === MarketLocationType.CONSUMER
                  ? t('MARKET_LOCATION_FEED_IN_CHART_MEASUREMENT_LINKED')
                  : t('MARKET_LOCATION_FEED_IN_CHART_MEASUREMENT'),
              step: 'left',
              color: '#2BAEFF',
              marker: {
                enabled: eachDayOfInterval(shownInterval ?? dataInterval).length < 30,
                radius: 1,
                symbol: 'circle',
              },
              softThreshold: true,
              data: linkedMeasures,
              zIndex: 1,
              dataGrouping: {
                units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
              },
              visible: true,
              showInNavigator: isFluctuating(marketLocation.linkedMarketLocation?.technologies),
            } satisfies Highcharts.SeriesOptionsType,
          ]
        : []),
      {
        type: 'line',
        id: SERIES_IDS.quality,
        yAxis: `y-${SERIES_IDS.quality}`,
        name: t(
          `MARKET_LOCATION_FEED_IN_CHART_MSCONS${marketLocation.type === MarketLocationType.CONSUMER ? '_LINKED' : ''}${isMobile ? '_MOBILE' : ''}`,
        ),
        color: '#4F9E30',
        lineWidth: 6,
        linecap: 'square',
        zoneAxis: 'x',
        zones: quality,
        marker: { enabled: false, symbol: 'square' },
        data: qualityData,
        zIndex: 0,
        dataGrouping: {
          units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
        },
        visible: true,
      },
      ...(linkedQualityData
        ? [
            {
              type: 'line',
              id: SERIES_IDS.linkedQuality,
              yAxis: `y-${SERIES_IDS.quality}`,
              name: t(
                `MARKET_LOCATION_FEED_IN_CHART_MSCONS${marketLocation.linkedMarketLocation?.type === MarketLocationType.CONSUMER ? '_LINKED' : ''}${isMobile ? '_MOBILE' : ''}`,
              ),
              color: '#4F9E30',
              lineWidth: 6,
              linecap: 'square',
              zoneAxis: 'x',
              zones: linkedQuality,
              marker: { enabled: false, symbol: 'square' },
              data: linkedQualityData,
              zIndex: 0,
              dataGrouping: {
                units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
              },
              visible: true,
            } satisfies Highcharts.SeriesOptionsType,
          ]
        : []),
      {
        type: 'line',
        id: SERIES_IDS.spotPrice,
        yAxis: `y-${SERIES_IDS.spotPrice}`,
        step: 'left',
        name: t('MARKET_LOCATION_FEED_IN_CHART_SPOT_PRICE'),
        zoneAxis: 'x',
        zones: [
          { value: latestHistoricalSpotPrice ?? toDate(dataInterval.start).getTime() },
          { dashStyle: 'ShortDot' },
        ],
        color: '#FFB210',
        marker: { enabled: false },
        softThreshold: true,
        data: spotPrices,
        dataGrouping: {
          units: isFifteenMinuteResolution ? [['minute', [15]]] : [['hour', [1]]],
        },
        visible: true,
      },
    ],
  };

  useEffect(() => {
    if (isMobile) {
      const todayTimestamp = new Date().setHours(0, 0, 0, 0);

      setTimeout(() => {
        Highcharts.charts[0]?.renderer.draw();
        Highcharts.charts[0]?.reflow();
        window.document
          .querySelector(`#timestamp-${todayTimestamp}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }, 1000);
    }
  }, [dataInterval, shownInterval, isMobile]);

  useEffect(() => {
    const xAxis = chartRef.current?.chart.xAxis;
    if (xAxis && shownInterval) {
      xAxis[0].setExtremes(getTime(shownInterval.start), getTime(shownInterval.end), true, true);
    }
  }, [shownInterval, dataInterval]);

  setTimeout(() => {
    chartRef.current?.chart.reflow();
  }, 350);

  return (
    <div className={isMobile ? '' : 'relative m-6 grid'}>
      {!shownInterval && (
        <Button
          className="absolute text-xs top-8 -left-2 z-10 min-w-0"
          variant="outlined"
          color="primary"
          data-testid="zoom-reset"
          onClick={() => chartRef.current?.chart.zoomOut()}
        >
          <ZoomOutIcon className="fill-green-200" />
        </Button>
      )}
      <HighchartsReact
        class={isMobile ? 'relative' : ''}
        options={options}
        constructorType="stockChart"
        highcharts={Highcharts}
        ref={chartRef}
      />
    </div>
  );
}
